/**
 * Plugin distribution (KiCad + EasyEDA installer assets) + plugin sync
 * status (the cookie-authed counterpart to the API-key-authed plugin
 * push endpoints).
 *
 * Mirrors ``backend/plugin_distribution/router.py`` and
 * ``backend/plugin_sync_routes/router.py``.
 */
import { env } from "@/env";
import apiClient from "@lib/axios";

// ── Plugin distribution ─────────────────────────────────────────────────────

export interface PluginReleaseAsset {
  name: string;
  size: number;
  download_url: string;
}

export interface PluginReleaseInfo {
  version: string;
  tag: string;
  name: string;
  published_at: string | null;
  notes: string | null;
  asset: PluginReleaseAsset | null;
}

export const pluginDistributionApi = {
  getLatestKicadRelease: async (): Promise<PluginReleaseInfo> => {
    const { data } = await apiClient.get<PluginReleaseInfo>(
      "/plugin/kicad/latest",
    );
    return data;
  },
  /** Direct (browser-fetchable) URL — frontend drops this on an
   *  `<a download>` so a click saves the GitHub-hosted zip. We hit the
   *  redirecting `/download` endpoint rather than GitHub directly so
   *  server logs see the tap. */
  downloadKicadUrl: (): string =>
    `${env.VITE_API_BASE_URL}/plugin/kicad/download`,

  getLatestEasyEdaRelease: async (): Promise<PluginReleaseInfo> => {
    const { data } = await apiClient.get<PluginReleaseInfo>(
      "/plugin/easyeda/latest",
    );
    return data;
  },
  /** Same shape as `downloadKicadUrl` — see that comment. */
  downloadEasyEdaUrl: (): string =>
    `${env.VITE_API_BASE_URL}/plugin/easyeda/download`,
};

// ── Plugin sync status ─────────────────────────────────────────────────────

/**
 * Drives the PluginSyncCard's "last synced" indicator. Cookie-authed —
 * not the same surface as the API-key-authed plugin endpoints.
 */
export interface PluginSyncStatus {
  last_pushed_at: string | null;
  last_snapshot_id: string | null;
  push_count: number;
}

export const pluginSyncApi = {
  getStatus: async (projectId: string): Promise<PluginSyncStatus> => {
    const { data } = await apiClient.get<PluginSyncStatus>(
      `/circuits/plugin/sync-status/${encodeURIComponent(projectId)}`,
    );
    return data;
  },
  /**
   * Direct (browser-fetchable) URL for pulling the project's current
   * committed schematic in the requested format. Cookie-authed via the
   * same session the SPA uses; we drop this on an ``<a download>`` so
   * a click in the browser saves the file.
   *
   * Default format is ``kicad_sch``; pass ``easyeda`` or ``altium_net``
   * to download the netlist for the corresponding tool. Requires the
   * backend's format-aware pull route (Phase 4).
   */
  pullDownloadUrl: (
    projectId: string,
    fmt: "kicad_sch" | "easyeda" | "altium_net" = "kicad_sch",
  ): string =>
    `${env.VITE_API_BASE_URL}/circuits/plugin/pull/${encodeURIComponent(
      projectId,
    )}?format=${encodeURIComponent(fmt)}`,
};
